/** Request bodies for the 2026-10-30 additions (types in growth.ts). */
import { z } from 'zod';
import { CONSENT_CHANNELS } from './contract.ts';
import { ADMIN_ROLES, AUTOMATION_KINDS, MODULES, PAID_TERMS, PAYMENT_METHODS, REPORT_KINDS, RFM_SEGMENTS, SEGMENT_KINDS } from './growth.ts';
import { localizedTextSchema, saleLineSchema } from './schemas.ts';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');
const money = z.number().min(0).max(100_000_000);
const id = z.string().min(1).max(64);
const optionalText = (max: number) => z.string().trim().max(max).optional().or(z.literal(''));
const limit = z.number().int().min(0).max(100_000_000).nullable();
const phoneCc = z.string().regex(/^\d{1,4}$/);
const phone = z.string().regex(/^\d{6,15}$/);
const otp = z.string().regex(/^\d{6}$/);

// ---- plans (admin portal)

export const planLimitsSchema = z.object({ members: limit, shops: limit, staff: limit, smsPerMonth: limit, aiImagesPerMonth: limit.default(null), newsletterEmailsPerMonth: limit.default(null) });

export const planSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(500),
  monthlyFee: money.nullable(),
  modules: z.array(z.enum(MODULES)).max(MODULES.length),
  limits: planLimitsSchema,
  sort: z.number().int().min(0).max(100000),
});
export type PlanInput = z.infer<typeof planSchema>;

/** A brand's plan, add-ons and limit overrides (absent key = use the plan's limit). */
export const brandPlanSchema = z.object({
  planId: id.nullable(),
  extraModules: z.array(z.enum(MODULES)).max(MODULES.length),
  limitOverrides: planLimitsSchema.partial(),
});
export type BrandPlanInput = z.infer<typeof brandPlanSchema>;

// ---- admin roles

export const adminRoleSchema = z.object({ role: z.enum(ADMIN_ROLES) });

// ---- two-step sign-in (enable uses the existing totpEnableSchema)

export const totpDisableSchema = z.object({ otp });

// ---- SMS

export const smsConfigSchema = z.object({
  provider: z.enum(['off', 'test', 'twilio']),
  twilioAccountSid: z.string().trim().regex(/^AC[0-9a-fA-F]{32}$/).nullable(),
  /** Write-only; empty or absent keeps the stored token. */
  twilioAuthToken: z.string().trim().max(128).optional(),
  sender: z.string().trim().max(20),
  allowedCountryCodes: z.array(phoneCc).min(1).max(20),
  dailyCap: z.number().int().min(1).max(100_000),
});
export type SmsConfigInput = z.infer<typeof smsConfigSchema>;

export const smsTestSchema = z.object({ phoneCc, phone });

export const googleLoginConfigSchema = z.object({
  enabled: z.boolean(),
  clientId: z
    .string()
    .trim()
    .regex(/^[\w.-]+\.apps\.googleusercontent\.com$/, 'ends with .apps.googleusercontent.com')
    .nullable(),
  /** Write-only; empty or absent keeps the stored secret. */
  clientSecret: z.string().trim().max(200).optional(),
});
export type GoogleLoginConfigInput = z.infer<typeof googleLoginConfigSchema>;

/** Member site: ask for a code by SMS. */
export const smsSendSchema = z.object({
  phoneCc,
  phone,
  purpose: z.enum(['login', 'register']),
  turnstileToken: z.string().max(4096).optional(),
});

export const smsLoginSchema = z.object({ phoneCc, phone, code: otp });

// ---- segments

export const segmentRulesSchema = z.object({
  tierIds: z.array(z.number().int()).max(50).optional(),
  joinedFrom: isoDate.nullable().optional(),
  joinedTo: isoDate.nullable().optional(),
  purchasedWithinDays: z.number().int().min(1).max(3650).nullable().optional(),
  noPurchaseForDays: z.number().int().min(1).max(3650).nullable().optional(),
  spendMin: money.nullable().optional(),
  spendMax: money.nullable().optional(),
  visitsMin: z.number().int().min(1).max(10000).nullable().optional(),
  pointsMin: z.number().min(0).max(100_000_000).nullable().optional(),
  pointsMax: z.number().min(0).max(100_000_000).nullable().optional(),
  birthdayMonths: z.array(z.number().int().min(1).max(12)).max(12).optional(),
  shopIds: z.array(id).max(50).optional(),
  sources: z.array(z.string().max(60)).max(50).optional(),
  consent: z.enum(CONSENT_CHANNELS).nullable().optional(),
  gender: z.enum(['M', 'F']).nullable().optional(),
  rfm: z.array(z.enum(RFM_SEGMENTS)).max(RFM_SEGMENTS.length).optional(),
  purchase: z
    .object({
      shopIds: z.array(id).min(1).max(50),
      within: z.number().int().min(1).max(3650),
      unit: z.enum(['day', 'month']),
      sku: z.string().trim().min(1).max(40),
      moreThan: z.number().int().min(0).max(1_000_000),
    })
    .nullable()
    .optional(),
});

export const segmentSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500),
  /** Absent: 每日更新 for a new segment, unchanged when editing. */
  kind: z.enum(SEGMENT_KINDS).optional(),
  rules: segmentRulesSchema,
});
/** Save the figures of 報表 / 進階分析 for a range as a 歷史快照. */
export const reportSnapshotSchema = z.object({
  kind: z.enum(REPORT_KINDS),
  from: isoDate,
  to: isoDate,
  shopId: id.nullable().optional(),
  title: z.string().trim().max(80).optional(),
});
export const segmentPreviewSchema = z.object({ rules: segmentRulesSchema });
export const segmentCouponSchema = z.object({ rewardId: id });

export const automationKindSchema = z.enum(AUTOMATION_KINDS);

// ---- shops and staff

export const staffUpdateSchema = z.object({
  role: z.enum(['brand_owner', 'brand_staff']),
  /** null = every shop. */
  shopIds: z.array(id).max(50).nullable(),
});

export const purchasePreviewSchema = z.object({ amount: z.number().positive().max(10_000_000), shopId: id.optional().or(z.literal('')), lines: z.array(saleLineSchema).max(50).optional() });

// ---- code batches

export const codeBatchSchema = z.object({
  name: z.string().trim().min(1).max(80),
  rewardId: id,
  quantity: z.number().int().min(1).max(10000),
  /** Letters / digits put in front of each code, e.g. "XMAS" → XMAS-7KQ2M9PA. */
  prefix: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{0,8}$/),
  claimStart: z.string().datetime({ offset: true }).nullable(),
  claimEnd: z.string().datetime({ offset: true }).nullable(),
  perMemberLimit: z.number().int().min(1).max(1000).nullable(),
});
/**
 * 公開優惠碼: one shared code (e.g. DAIMING10) for a coupon, posted on social media. Each member may use
 * it once; `limit` is how many members may use it.
 */
export const publicCodeSchema = z
  .object({
    code: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{4,20}$/),
    rewardId: id,
    startAt: z.string().datetime({ offset: true }).nullable(),
    endAt: z.string().datetime({ offset: true }),
    limit: z.number().int().min(1).max(1_000_000),
    active: z.boolean(),
  })
  .refine((v) => !v.startAt || Date.parse(v.endAt) > Date.parse(v.startAt), { message: 'end before start', path: ['endAt'] });
/** Member site: add a code to 我的優惠券. */
export const claimCodeSchema = z.object({ code: z.string().trim().toUpperCase().regex(/^[A-Z0-9-]{4,24}$/) });

// ---- payments at the counter

const payment = { paymentMethod: z.enum(PAYMENT_METHODS), paymentRef: optionalText(80), shopId: id.optional().or(z.literal('')) };

// ---- paid membership

/** LPSS-44: a 付費會籍 level. LPSS-49: `earn` (較高積分比例) and `discount` (固定折扣, 1–99 = % paid) each optional, both allowed. */
export const paidPlanSchema = z.object({
  name: localizedTextSchema,
  description: localizedTextSchema,
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  sku: z
    .string()
    .trim()
    .max(40)
    .transform((s) => s || null)
    .nullable(),
  price: money,
  term: z.enum(PAID_TERMS),
  months: z.number().int().min(1).max(120),
  earn: z.object({ amount: z.number().positive().max(1_000_000), points: z.number().min(0).max(1_000_000) }).nullable(),
  discount: z.number().int().min(1).max(99).nullable(),
  bonusPoints: z.number().int().min(0).max(1_000_000),
  rewardId: id.nullable(),
  active: z.boolean(),
  sort: z.number().int().min(0).max(100000),
});
export const sellPaidMembershipSchema = z.object({ planId: id, ...payment });
export const cancelSchema = z.object({ note: z.string().trim().min(1).max(300) });

// ---- stored value

export const topupSchema = z.object({ amount: z.number().positive().max(1_000_000), ...payment });
/** Pay with the balance; `earnPoints` also records the sale for points / stamps / level. */
export const storedValueSpendSchema = z.object({
  amount: z.number().positive().max(1_000_000),
  invoiceNo: optionalText(60),
  shopId: id.optional().or(z.literal('')),
  note: optionalText(300),
  earnPoints: z.boolean(),
});
export const storedValueAdjustSchema = z.object({ amount: z.number().min(-1_000_000).max(1_000_000).refine((v) => v !== 0, 'not 0'), note: z.string().trim().min(1).max(300) });

// ---- gift cards

export const giftCardProductSchema = z.object({
  name: localizedTextSchema,
  amount: z.number().positive().max(1_000_000),
  price: money,
  validMonths: z.number().int().min(1).max(120).nullable(),
  active: z.boolean(),
  sort: z.number().int().min(0).max(100000),
});
export const sellGiftCardsSchema = z.object({
  productId: id,
  quantity: z.number().int().min(1).max(50),
  buyerName: optionalText(80),
  buyerPhone: optionalText(20),
  ...payment,
});
/** Accepts XXXX-XXXX-XXXX or the 12 characters with spaces; the result is the 12 characters. */
export const giftCardCodeSchema = z.object({
  code: z
    .string()
    .max(30)
    .transform((v) => v.toUpperCase().replace(/[\s-]/g, ''))
    .pipe(z.string().regex(/^[A-Z0-9]{12}$/)),
});
